import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import apiClient from "../apiClient";
import { formatMoney } from "../formatMoney";
import PageContext from "../PageContext.jsx";

const AssetsUpdate = () => {
  const [currencies, setCurrencies] = useState([]);
  const [asset, setAsset] = useState({
    Description: "",
    PurchaseValue: "",
    CurrentValue: "",
    CurrentValuationDate: "",
    CurrencyID: "",
  });
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const assetId = location.pathname.split("/")[2];
  const sortedCurrencies = useMemo(
    () => [...currencies].sort((a, b) => (a.Description || "").localeCompare(b.Description || "")),
    [currencies]
  );
  const selectedCurrency = useMemo(
    () => currencies.find((currency) => String(currency.CurrencyID) === String(asset.CurrencyID)) || null,
    [currencies, asset.CurrencyID]
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [assetRes, currenciesRes] = await Promise.all([apiClient.get("/assets"), apiClient.get("/currencies")]);
        const currenciesRows = Array.isArray(currenciesRes.data) ? currenciesRes.data : [];
        const sorted = [...currenciesRows].sort((a, b) => (a.Description || "").localeCompare(b.Description || ""));
        setCurrencies(sorted);

        const assetsRows = Array.isArray(assetRes.data) ? assetRes.data : [];
        const selectedAsset = assetsRows.find((item) => String(item.AssetID) === String(assetId));
        if (selectedAsset) {
          setAsset({
            Description: selectedAsset.Description || "",
            PurchaseValue: selectedAsset.PurchaseValue != null ? String(selectedAsset.PurchaseValue) : "",
            CurrentValue: selectedAsset.CurrentValue != null ? String(selectedAsset.CurrentValue) : "",
            CurrentValuationDate: selectedAsset.CurrentValuationDate || "",
            CurrencyID: selectedAsset.CurrencyID || sorted[0]?.CurrencyID || "",
          });
        }
      } catch (err) {
        setError("Could not load asset.");
      }
    };

    fetchData();
  }, [assetId]);

  const handleChange = (e) => {
    setAsset((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!asset.Description?.trim() || !asset.CurrencyID) {
      setError("Description and currency are required.");
      return;
    }

    try {
      await apiClient.put(`/assets/${assetId}`, {
        Description: asset.Description,
        PurchaseValue: Number(asset.PurchaseValue || 0),
        CurrentValue: Number(asset.CurrentValue || 0),
        CurrentValuationDate: asset.CurrentValuationDate || null,
        CurrencyID: Number(asset.CurrencyID),
      });
      navigate("/Assets");
    } catch (err) {
      setError("Could not update asset. Please try again.");
    }
  };

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Update Asset</h1>
          <p className="page-subtitle"><Link to="/">Home</Link> / <Link to="/Assets">Assets</Link> / Update</p>
        </div>
      </header>
      <PageContext
        items={[
          { label: "Home", to: "/" },
          { label: "Assets", to: "/Assets" },
          { label: "Update Asset" },
        ]}
        note={asset.Description ? `Editing ${asset.Description}.` : "Update the selected asset details and valuation."}
      />

      <section className="panel form-card">
        <div className="form">
          <label htmlFor="asset-description">Description</label>
          <input id="asset-description" type="text" name="Description" value={asset.Description} onChange={handleChange} />

          <label htmlFor="asset-purchase-value">Purchase Value</label>
          <input id="asset-purchase-value" type="number" min="0" step="0.01" name="PurchaseValue" value={asset.PurchaseValue} onChange={handleChange} />
          <div className="form-value-preview">
            Display: {formatMoney(asset.PurchaseValue || 0, selectedCurrency?.Description)}
          </div>

          <label htmlFor="asset-current-value">Current Value</label>
          <input id="asset-current-value" type="number" min="0" step="0.01" name="CurrentValue" value={asset.CurrentValue} onChange={handleChange} />
          <div className="form-value-preview">
            Display: {formatMoney(asset.CurrentValue || 0, selectedCurrency?.Description)}
          </div>

          <label htmlFor="asset-current-valuation-date">Date Of Current Valuation</label>
          <input id="asset-current-valuation-date" type="date" name="CurrentValuationDate" value={asset.CurrentValuationDate} onChange={handleChange} />

          <label htmlFor="asset-currency">Currency</label>
          <select id="asset-currency" name="CurrencyID" value={asset.CurrencyID} onChange={handleChange}>
            {sortedCurrencies.map((currency) => (
              <option key={currency.CurrencyID} value={currency.CurrencyID}>
                {currency.Description}
              </option>
            ))}
          </select>

          <div className="form-actions">
            <button className="btn" onClick={handleSubmit}>Update Asset</button>
            <Link className="btn-link" to="/Assets">Cancel</Link>
          </div>

          {error && <p className="notice notice-error">{error}</p>}
        </div>
      </section>
    </div>
  );
};

export default AssetsUpdate;
